import sanitizeHtmlLib from 'sanitize-html';

// Shared XSS sanitizer for recruiter-authored job/position description HTML that is
// rendered via `dangerouslySetInnerHTML`. Uses the robust `sanitize-html` allowlist —
// the SAME security config the public job-detail page already uses
// (`(app)/jobs/[slug]/job-detail-client.tsx`). Centralised here so every sink stays in
// lockstep, replacing the bypassable regex denylist that was inlined in admin/jobs edit
// and the no-sanitization render on the token-gated approval page.
//
// (The MS-Office paste cleanup in job-detail-client is content normalisation, not
//  security, and intentionally stays local to that display path.)
const JOB_HTML_OPTIONS: sanitizeHtmlLib.IOptions = {
  allowedTags: sanitizeHtmlLib.defaults.allowedTags.concat([
    'img', 'h1', 'h2', 'h3', 'h4', 'h5', 'h6', 'span', 'div', 'br', 'hr',
    'table', 'thead', 'tbody', 'tr', 'th', 'td',
  ]),
  allowedAttributes: {
    ...sanitizeHtmlLib.defaults.allowedAttributes,
    '*': ['class', 'id'],
    a: ['href', 'target', 'rel'],
    img: ['src', 'alt', 'width', 'height'],
  },
  allowedSchemes: ['https', 'http', 'mailto'],
  disallowedTagsMode: 'discard',
  transformTags: {
    a: sanitizeHtmlLib.simpleTransform('a', { rel: 'noopener noreferrer', target: '_blank' }),
  },
};

/** Sanitize recruiter-authored job/position description HTML for safe rendering. */
export function sanitizeJobHtml(html: string | null | undefined): string {
  if (!html) return '';
  return sanitizeHtmlLib(html, JOB_HTML_OPTIONS);
}

// Public legal document HTML (Agreements module, §4.14 / DES-8) — the
// "readable copy" of a Policy or the Standard Terms. The HTML is always
// generated by this app's own markdown renderer (src/lib/markdown-lite.ts)
// from the API's markdown text, never accepted as HTML from the API
// directly ("no raw HTML passthrough"). This allowlist is deliberately
// tighter than JOB_HTML_OPTIONS — legal text needs headings/paragraphs/
// lists/emphasis/links and pipe tables only, never img/span, since those
// would only be reachable through a bug in the renderer, not real content.
//
// Tables: `table/thead/tbody/tr/th/td` carry NO attributes at all. The one
// `div` the renderer emits is the phone-width scroll wrapper around a table,
// and its only permitted attribute is `class="overflow-x-auto"`: any other
// class value and any other attribute (style, id, on*) is stripped
// (`allowedClasses` pins the value, not just the attribute name).
const LEGAL_HTML_OPTIONS: sanitizeHtmlLib.IOptions = {
  allowedTags: [
    'h2', 'h3', 'p', 'ul', 'ol', 'li', 'strong', 'em', 'a', 'hr', 'blockquote',
    'table', 'thead', 'tbody', 'tr', 'th', 'td', 'div',
  ],
  allowedAttributes: {
    // rel/target must stay allowed too, or sanitize-html strips them right
    // back off after transformTags adds them below.
    a: ['href', 'id', 'rel', 'target'],
    h2: ['id'],
    h3: ['id'],
    div: ['class'],
  },
  allowedClasses: {
    div: ['overflow-x-auto'],
  },
  allowedSchemes: ['https', 'http', 'mailto'],
  disallowedTagsMode: 'discard',
  transformTags: {
    a: sanitizeHtmlLib.simpleTransform('a', { rel: 'noopener noreferrer', target: '_blank' }),
  },
};

/** Sanitize the HTML this app's own markdown-lite renderer produced for a
 *  public legal document body. Defense-in-depth: the renderer already
 *  escapes source text before building tags, so this is a second gate, not
 *  the only one. */
export function sanitizeLegalHtml(html: string | null | undefined): string {
  if (!html) return '';
  return sanitizeHtmlLib(html, LEGAL_HTML_OPTIONS);
}
